import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Body1Strong,
  Body2,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  Option,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeftRegular, CheckmarkRegular, DismissRegular } from '@fluentui/react-icons';

import { useDocumentTitle } from '@shared/hooks';
import { ToastContext } from '@shared/contexts';
import { ConfirmDialog } from '@shared/components';

import { SCOPE_TYPE_OPTIONS, QMS_AREA_OPTIONS } from '../constants';
import { MOCK_PRODUCT_NAMES, getTemplateById } from '../data';
import { CREATION_METHOD, TChangeTemplate, TCreationMethod, TScopeType, TTemplateInputValue } from '../models';
import { getMissingRequiredInputs, getUnknownTemplateTokens } from '../utils';
import {
  CreationMethodCards,
  PreparedByTemplate,
  PreviousChangePicker,
  SourceRecordPicker,
  TemplateGallery,
  TemplateSetupForm,
} from '../components/create';
import { MultiSelectDropdown, SectionCard, useChangeV2Styles } from '../components/shared';
import { ChangeV2PageScaffold } from '../layouts/ChangeV2PageScaffold';
import { getChangeV2DashboardPath, getChangeV2RequestPath } from '../helpers';
import { useChangeV2Store } from '../state/ChangeV2Store';

type TConfirmState = NonNullable<Parameters<typeof ConfirmDialog>[0]['state']>;

const useStyles = makeStyles({
  header: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  muted: { color: tokens.colorNeutralForeground3 },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  footerRight: { display: 'flex', gap: tokens.spacingHorizontalS },
  templateLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  panelHeader: { display: 'flex', flexDirection: 'column', gap: '2px' },
  emptyPanel: {
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

const CreateChangePage = () => {
  useDocumentTitle('Create change');
  const classes = useStyles();
  const shared = useChangeV2Styles();
  const navigate = useNavigate();
  const { show } = useContext(ToastContext);
  const { templates, previousChanges, createDraft } = useChangeV2Store();

  const [step, setStep] = useState<1 | 2>(1);
  const [method, setMethod] = useState<TCreationMethod>();
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState('');
  const [products, setProducts] = useState<string[]>([]);
  const [qmsAreas, setQmsAreas] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState<string>();
  const [inputValues, setInputValues] = useState<Record<string, TTemplateInputValue>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inputsDirty, setInputsDirty] = useState(false);
  const [previousChangeId, setPreviousChangeId] = useState<string>();
  const [sourceType, setSourceType] = useState('');
  const [sourceRecordId, setSourceRecordId] = useState<string>();
  const [confirmState, setConfirmState] = useState<TConfirmState>();

  const selectedTemplate = getTemplateById(templates, templateId);
  const isTemplateMethod = method === CREATION_METHOD.TEMPLATE;

  // Template inputs describe facts about the new Change, so a new setup always
  // starts empty. Real values belong only to the Change being instantiated.
  const seedInputValues = (template?: TChangeTemplate): Record<string, TTemplateInputValue> => {
    if (!template) return {};
    const seeded: Record<string, TTemplateInputValue> = {};
    for (const input of template.creationInputs ?? []) {
      seeded[input.id] = null;
    }
    return seeded;
  };

  const applyTemplateSelection = (id: string) => {
    setTemplateId(id);
    setInputValues(seedInputValues(getTemplateById(templates, id)));
    setTouched({});
    setInputsDirty(false);
  };

  const handleSelectTemplate = (id: string) => {
    if (id === templateId) return;
    // Switching resets template-specific setup values; confirm only once the user
    // has entered data so an accidental switch does not discard their inputs.
    if (templateId && inputsDirty) {
      const dialog: TConfirmState = {
        isOpen: true,
        title: 'Switch template?',
        content:
          'Switching clears the details you entered for the current template. The template list stays available.',
        buttonLabel: 'Switch template',
        onConfirm: async () => {
          applyTemplateSelection(id);
          setConfirmState({ ...dialog, isOpen: false });
        },
      };
      setConfirmState(dialog);
      return;
    }
    applyTemplateSelection(id);
  };

  const handleInputChange = (inputId: string, value: TTemplateInputValue) => {
    setInputValues((prev) => ({ ...prev, [inputId]: value }));
    setInputsDirty(true);
  };

  const handleInputBlur = (inputId: string) => setTouched((prev) => ({ ...prev, [inputId]: true }));

  const missingRequiredInputs =
    isTemplateMethod && selectedTemplate
      ? getMissingRequiredInputs(selectedTemplate.creationInputs ?? [], inputValues)
      : [];
  // A misconfigured template (a token with no matching input) must never produce a
  // draft; block creation and let the development warning surface the problem.
  const hasUnknownTokens =
    isTemplateMethod && selectedTemplate ? getUnknownTemplateTokens(selectedTemplate, inputValues).length > 0 : false;

  const canCreate =
    method === CREATION_METHOD.BLANK ||
    (method === CREATION_METHOD.TEMPLATE &&
      Boolean(templateId) &&
      missingRequiredInputs.length === 0 &&
      !hasUnknownTokens) ||
    (method === CREATION_METHOD.PREVIOUS && Boolean(previousChangeId)) ||
    (method === CREATION_METHOD.SOURCE && Boolean(sourceRecordId));

  const handleCreate = () => {
    if (!method) return;
    const draft = createDraft({
      creationMethod: method,
      initialTitle: title.trim() || undefined,
      initialScopeType: (scope || undefined) as TScopeType | undefined,
      initialProducts: products,
      initialQmsAreas: qmsAreas,
      templateId,
      templateInputValues: isTemplateMethod ? inputValues : undefined,
      previousChangeId,
      sourceRecordId,
    });
    show('success', 'Draft created. Complete the request to submit it.');
    navigate(getChangeV2RequestPath(draft.id));
  };

  const draftBasics = (
    <div className={shared.fieldGrid}>
      <Field label="Title">
        <Input value={title} onChange={(_, data) => setTitle(data.value)} placeholder="Short descriptive title" />
      </Field>
      <Field label="Scope">
        <Dropdown
          placeholder="Select a scope"
          selectedOptions={scope ? [scope] : []}
          value={scope}
          onOptionSelect={(_, data) => setScope((data.optionValue as string) ?? '')}
        >
          {SCOPE_TYPE_OPTIONS.map((option) => (
            <Option key={option} value={option} text={option}>
              {option}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field label="Affected products">
        <MultiSelectDropdown
          value={products}
          options={MOCK_PRODUCT_NAMES}
          onChange={setProducts}
          placeholder="Select products"
          ariaLabel="Affected products"
        />
      </Field>
      <Field label="Affected QMS areas">
        <MultiSelectDropdown
          value={qmsAreas}
          options={QMS_AREA_OPTIONS}
          onChange={setQmsAreas}
          placeholder="Select QMS areas"
          ariaLabel="Affected QMS areas"
        />
      </Field>
    </div>
  );

  const templateRightColumn = selectedTemplate ? (
    <div className={classes.rightColumn}>
      <div className={classes.panel}>
        <div className={classes.panelHeader}>
          <Body1Strong>Set up this change</Body1Strong>
          <Caption1 className={classes.muted}>
            Enter the details needed to prepare the draft. You can edit the draft afterwards.
          </Caption1>
        </div>
        {selectedTemplate.creationInputs && selectedTemplate.creationInputs.length > 0 ? (
          <TemplateSetupForm
            inputs={selectedTemplate.creationInputs}
            values={inputValues}
            onChange={handleInputChange}
            touched={touched}
            onBlur={handleInputBlur}
            showAllErrors={false}
          />
        ) : (
          <Caption1 className={classes.muted}>This template needs no additional details.</Caption1>
        )}
      </div>

      <div className={classes.panel}>
        <PreparedByTemplate template={selectedTemplate} inputValues={inputValues} />
      </div>
    </div>
  ) : (
    <div className={classes.emptyPanel}>
      <Body2 className={classes.muted}>Select a template to set up the change.</Body2>
    </div>
  );

  return (
    <ChangeV2PageScaffold breadcrumbTail={[{ name: 'Create change' }]}>
      <div className={classes.header}>
        <Title2>Create change</Title2>
        <Body2 className={classes.muted}>
          {step === 1 ? 'Choose how to start.' : 'Configure the draft. You can refine everything on the request.'}
        </Body2>
      </div>

      {step === 1 ? (
        <SectionCard title="Creation method" description="Pick a starting point for the new change.">
          <CreationMethodCards value={method} onSelect={setMethod} />
        </SectionCard>
      ) : isTemplateMethod ? (
        <SectionCard
          title="Template"
          description="Choose a template and enter the few facts it needs before creating the draft."
        >
          <div className={classes.templateLayout}>
            <TemplateGallery templates={templates} selectedId={templateId} onSelect={handleSelectTemplate} />
            {templateRightColumn}
          </div>
        </SectionCard>
      ) : (
        <>
          <SectionCard
            title="Draft basics"
            description="Optional starting details. Required fields live on the request."
          >
            {draftBasics}
          </SectionCard>

          {method === CREATION_METHOD.PREVIOUS ? (
            <SectionCard title="Previous change" description="Reuse the structure of a past change.">
              <PreviousChangePicker
                previousChanges={previousChanges}
                selectedId={previousChangeId}
                onSelect={setPreviousChangeId}
              />
            </SectionCard>
          ) : null}

          {method === CREATION_METHOD.SOURCE ? (
            <SectionCard
              title="Source record"
              description="Start from a source record. The title and reason are prefilled and stay editable."
            >
              <SourceRecordPicker
                sourceType={sourceType}
                onTypeChange={setSourceType}
                selectedRecordId={sourceRecordId}
                onRecordSelect={setSourceRecordId}
              />
            </SectionCard>
          ) : null}
        </>
      )}

      <div className={classes.footer}>
        {step === 2 ? (
          <Button appearance="secondary" icon={<ArrowLeftRegular />} onClick={() => setStep(1)}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <div className={classes.footerRight}>
          <Button appearance="subtle" icon={<DismissRegular />} onClick={() => navigate(getChangeV2DashboardPath())}>
            Cancel
          </Button>
          {step === 1 ? (
            <Button appearance="primary" disabled={!method} onClick={() => setStep(2)}>
              Next
            </Button>
          ) : (
            <Button appearance="primary" icon={<CheckmarkRegular />} disabled={!canCreate} onClick={handleCreate}>
              Create draft
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog state={confirmState} setState={setConfirmState} />
    </ChangeV2PageScaffold>
  );
};

export default CreateChangePage;
